import { metaTagContent } from '@/helpers/documentHelpers'

export interface FormSubmitOptions {
  action: string
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | string
  data?: Record<string, any>
  target?: '_self' | '_blank' | '_parent' | '_top' | string
}

/**
 * Programmatically submits a form with native browser behavior.
 * Creates a temporary form element, populates it with data, and submits it,
 * causing a full page navigation just like a native form submission.
 *
 * Automatically includes CSRF token for POST/PUT/PATCH/DELETE requests.
 *
 * @example
 * ```typescript
 * // Simple POST (CSRF token included automatically)
 * submitForm({
 *   action: '/users/sign_in',
 *   data: { 'user[email]': 'test@example.com', 'user[password]': '123' }
 * })
 *
 * // With custom method and target
 * submitForm({
 *   action: '/search',
 *   method: 'GET',
 *   data: { q: 'vacation' },
 *   target: '_blank'
 * })
 * ```
 */
export function submitForm(options: FormSubmitOptions): void {
  const { action, method = 'POST', data = {}, target } = options

  // Automatically include CSRF token for non-GET requests
  const formData = { ...data }
  if (method.toUpperCase() !== 'GET' && !formData.authenticity_token) {
    const csrfToken = metaTagContent('csrf-token')
    if (csrfToken) {
      formData.authenticity_token = csrfToken
    }
  }

  // Create form element
  const form = document.createElement('form')
  form.method = method
  form.action = action
  if (target) {
    form.target = target
  }

  // Add all data as hidden inputs
  Object.entries(formData).forEach(([name, value]) => {
    // Skip undefined and null values
    if (value === undefined || value === null) {
      return
    }

    // Handle arrays (e.g., for multiple select values)
    if (Array.isArray(value)) {
      value.forEach(item => {
        addHiddenInput(form, name, item)
      })
    } else {
      addHiddenInput(form, name, value)
    }
  })

  // Append to body, submit, and cleanup
  document.body.appendChild(form)
  form.submit()

  // Cleanup happens after navigation starts (won't execute if navigation is immediate)
  // But good practice for any edge cases
  setTimeout(() => {
    if (document.body.contains(form)) {
      document.body.removeChild(form)
    }
  }, 0)
}

/**
 * Helper to add a hidden input to a form
 */
function addHiddenInput(form: HTMLFormElement, name: string, value: any): void {
  const input = document.createElement('input')
  input.type = 'hidden'
  input.name = name

  // Handle different value types
  if (typeof value === 'object') {
    input.value = JSON.stringify(value)
  } else {
    input.value = String(value)
  }

  form.appendChild(input)
}
